import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { CheckIcon, BookOpenIcon, UsersIcon, DumbbellIcon, ClockIcon, BrainIcon, HeartIcon } from 'lucide-react';
export function SalesLetterPromise() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const promises = ['Reprogramar tu diálogo interno (y dejar de sabotearte)', 'Cambiar creencias que gobiernan tu vida en piloto automático', 'Romper patrones repetidos (relaciones, dinero, hábitos, autoestima)', 'Elevar tu estado emocional sin fingir estar bien', 'Tomar decisiones con una brújula interna más clara: tu intuición entrenada'];
  const chapterFeatures = [{
    icon: BookOpenIcon,
    text: 'Una explicación clara'
  }, {
    icon: UsersIcon,
    text: 'Ejemplos cotidianos (dinero, relaciones, trabajo, autoestima)'
  }, {
    icon: DumbbellIcon,
    text: 'Ejercicios mixtos: 2 cortos (5–10 min) + 1 profundo (20–40 min)'
  }, {
    icon: ClockIcon,
    text: 'Una acción de 24 horas para sellar el cambio'
  }];
  return <section ref={ref} className="w-full bg-gradient-to-b from-[#0a0e27] via-[#1a1a4e] to-[#2d1b69] py-20 px-4 md:py-28" data-id="element-220">
      <div className="max-w-5xl mx-auto" data-id="element-221">
        {/* Promise Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-20" data-id="element-222">
          <h2 className="font-playfair text-2xl md:text-3xl lg:text-4xl text-[#d4a853] text-center mb-4" data-id="element-223">
            La promesa de este libro
          </h2>
          <p className="font-dm-sans text-lg text-[#f5f0e8]/70 text-center mb-10" data-id="element-224">
            (sin magia, con verdad)
          </p>

          <p className="font-dm-sans text-lg text-[#f5f0e8]/90 text-center mb-8" data-id="element-225">
            Este eBook te muestra cómo alinear tu intuición con las 7 Leyes
            Universales (espiritualidad práctica, aplicada) para:
          </p>

          <div className="space-y-4 max-w-2xl mx-auto mb-10" data-id="element-226">
            {promises.map((promise, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.4,
            delay: 0.2 + index * 0.1
          }} className="flex items-start gap-4" data-id="element-227">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#d4a853]/20 flex items-center justify-center mt-0.5" data-id="element-228">
                  <CheckIcon className="w-4 h-4 text-[#d4a853]" data-id="element-229" />
                </div>
                <p className="font-dm-sans text-lg text-[#f5f0e8]/90" data-id="element-230">
                  {promise}
                </p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={isInView ? {
          opacity: 1,
          scale: 1
        } : {}} transition={{
          duration: 0.5,
          delay: 0.7
        }} className="bg-gradient-to-r from-[#d4a853]/10 via-[#d4a853]/20 to-[#d4a853]/10 border border-[#d4a853]/30 rounded-xl p-6 md:p-8 text-center" data-id="element-231">
            <p className="font-playfair text-xl md:text-2xl text-[#f0c75e] italic" data-id="element-232">
              "No es un libro para inspirarte una semana. Es un libro para
              entrenarte… y que se note en tu día a día."
            </p>
          </motion.div>
        </motion.div>

        {/* Bridge Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="mb-20" data-id="element-233">
          <h2 className="font-playfair text-2xl md:text-3xl lg:text-4xl text-[#f5f0e8] text-center mb-10" data-id="element-234">
            ¿Por qué <span className="text-[#d4a853]" data-id="element-235">"intuición"</span> +{' '}
            <span className="text-[#d4a853]" data-id="element-236">"Leyes Universales"</span>?
          </h2>

          <p className="font-dm-sans text-lg text-[#f5f0e8]/80 text-center mb-10" data-id="element-237">
            Porque mucha gente intenta cambiar su vida desde uno de estos
            extremos:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-238">
            {/* Rational Mind Card */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.5
          }} className="bg-gradient-to-br from-red-900/20 to-red-950/30 border border-red-500/20 rounded-xl p-6" data-id="element-239">
              <div className="flex items-center gap-3 mb-4" data-id="element-240">
                <BrainIcon className="w-6 h-6 text-red-400" data-id="element-241" />
                <h3 className="font-playfair text-xl text-red-300" data-id="element-242">
                  Solo mente racional (sin alma)
                </h3>
              </div>
              <p className="font-dm-sans text-[#f5f0e8]/80" data-id="element-243">
                Metas, listas, productividad… pero por dentro:{' '}
                <span className="text-red-300" data-id="element-244">
                  miedo, ansiedad, autoexigencia, diálogo interno agresivo.
                </span>
              </p>
            </motion.div>

            {/* Spirituality Card */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={isInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.6
          }} className="bg-gradient-to-br from-blue-900/20 to-blue-950/30 border border-blue-500/20 rounded-xl p-6" data-id="element-245">
              <div className="flex items-center gap-3 mb-4" data-id="element-246">
                <HeartIcon className="w-6 h-6 text-blue-400" data-id="element-247" />
                <h3 className="font-playfair text-xl text-blue-300" data-id="element-248">
                  Solo espiritualidad (sin tierra)
                </h3>
              </div>
              <p className="font-dm-sans text-[#f5f0e8]/80" data-id="element-249">
                Afirmaciones, "manifestación", visualizar… pero{' '}
                <span className="text-blue-300" data-id="element-250">
                  sin hábitos, sin coherencia, sin decisiones nuevas, sin acción
                  real.
                </span>
              </p>
            </motion.div>
          </div>

          {/* Bridge Element */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.8
        }} className="relative" data-id="element-251">
            <div className="absolute left-1/2 -translate-x-1/2 -top-4 w-1 h-8 bg-gradient-to-b from-transparent to-[#d4a853]" data-id="element-252" />
            <div className="bg-gradient-to-r from-[#d4a853]/20 via-[#d4a853]/30 to-[#d4a853]/20 border-2 border-[#d4a853]/50 rounded-xl p-6 md:p-8 text-center" data-id="element-253">
              <p className="font-playfair text-xl md:text-2xl text-[#d4a853]" data-id="element-254">
                Este eBook es el puente:
              </p>
              <p className="font-dm-sans text-lg md:text-xl text-[#f5f0e8] mt-3" data-id="element-255">
                <span className="text-[#f0c75e] font-semibold" data-id="element-256">Intuición</span>{' '}
                (dirección interna) +{' '}
                <span className="text-[#f0c75e] font-semibold" data-id="element-257">Leyes</span>{' '}
                (estructura de vida) ={' '}
                <span className="text-[#d4a853] font-bold" data-id="element-258">
                  transformación real
                </span>
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Differentiator Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.4
      }} data-id="element-259">
          <h2 className="font-playfair text-2xl md:text-3xl lg:text-4xl text-[#d4a853] text-center mb-4" data-id="element-260">
            Lo que hace diferente a este eBook
          </h2>

          <p className="font-dm-sans text-lg text-[#f5f0e8]/90 text-center mb-10" data-id="element-261">
            No está escrito para "explicarte" conceptos.{' '}
            <span className="text-[#f0c75e]" data-id="element-262">
              Está escrito para cambiarte con práctica.
            </span>
          </p>

          <p className="font-dm-sans text-lg text-[#f5f0e8]/80 text-center mb-8" data-id="element-263">
            Cada capítulo incluye:
          </p>

          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto mb-10" data-id="element-264">
            {chapterFeatures.map((feature, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.4,
            delay: 0.6 + index * 0.1
          }} className="flex items-start gap-4 bg-[#0a0e27]/40 rounded-lg p-4" data-id="element-265">
                <feature.icon className="w-6 h-6 text-[#d4a853] flex-shrink-0 mt-0.5" data-id="element-266" />
                <p className="font-dm-sans text-[#f5f0e8]/90" data-id="element-267">{feature.text}</p>
              </motion.div>)}
          </div>

          <p className="font-dm-sans text-lg text-[#f5f0e8] text-center" data-id="element-268">
            Porque tu mente cambia cuando ve evidencia.{' '}
            <span className="text-[#d4a853]" data-id="element-269">
              Y la evidencia aparece cuando tú actúas.
            </span>
          </p>
        </motion.div>
      </div>
    </section>;
}